/**
 * Aircraft performance database — read model.
 *
 * Rust `aircraft_db_apply` is the writer. This module loads the shipped base
 * file for the desk to display and to send back on the company snapshot.
 * Lookup and overlay merge stay in Rust.
 */
import base from "./data/aircraftBase.json";
import type { AircraftSpec } from "./types";

export type PerfSource = "manual" | "user" | "derived";

export type SourcedNumber = {
  value: number | null;
  source: PerfSource;
  note?: string;
};

export type SourcedText = {
  value: string | null;
  source: PerfSource;
  note?: string;
};

export type AircraftTypeRecord = {
  icaoType: string;
  origin: "base" | "user";
  name: SourcedText;
  cruiseKt: SourcedNumber;
  longRangeCruiseKt: SourcedNumber;
  /** Average cruise the Scheme speed line uses. Blank until entered. */
  averageCruiseKt: SourcedNumber;
  /** Cruise Mach. Blank until entered. */
  cruiseMach: SourcedNumber;
  /** Climb profile, feet per minute. Blank until entered. */
  climbRateFpm: SourcedNumber;
  /** Descent profile, feet per minute. Blank until entered. */
  descentRateFpm: SourcedNumber;
  rangeNm: SourcedNumber;
  ferryRangeNm: SourcedNumber;
  /** Planning burn. Pounds per hour. Gallons convert with `fuelDensityLbPerGal`. */
  fuelBurnLbHr: SourcedNumber;
  maxFuelLb: SourcedNumber;
  /** Pounds per US gallon. H25B is 6.7, derived from the EFB screen. */
  fuelDensityLbPerGal: SourcedNumber;
  mtowLb: SourcedNumber;
  mlwLb: SourcedNumber;
  mzfwLb: SourcedNumber;
  mrwLb: SourcedNumber;
  /** Blank until the operator enters it. */
  oewLb: SourcedNumber;
  maxPayloadLb: SourcedNumber;
  maxPax: SourcedNumber;
  crewSeats: SourcedNumber;
  ceilingFt: SourcedNumber;
  takeoffDistanceFt: SourcedNumber;
  landingDistanceFt: SourcedNumber;
  engines: SourcedText;
  mpiIntervalHours: SourcedNumber;
  cziIntervalHours: SourcedNumber;
};

export type AircraftDatabaseDesk = {
  revision: number;
  /** Effective rows: shipped base with the user overlay applied. */
  types: AircraftTypeRecord[];
  /** User documents only. The base file is not rewritten. */
  overlay: unknown[];
};

export const AIRCRAFT_DB_COMMAND = "aircraft_db_apply";
export const AIRCRAFT_DB_EXPORT_COMMAND = "aircraft_db_export";
export const AIRCRAFT_DB_EVENT = "aircraft-db://changed";
export const SIM_PERF_LOOKUP_COMMAND = "sim_perf_lookup";

export type AircraftDbChanged = {
  revision: number;
  types: AircraftTypeRecord[];
  overlay: unknown[];
};

const SOURCES: readonly PerfSource[] = ["manual", "user", "derived"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function sourcedNumber(raw: unknown, fallback: SourcedNumber): SourcedNumber {
  if (!isRecord(raw)) return fallback;
  const value = raw.value == null ? null : Number(raw.value);
  const source = SOURCES.includes(raw.source as PerfSource) ? (raw.source as PerfSource) : fallback.source;
  const note = typeof raw.note === "string" && raw.note.trim() ? raw.note : fallback.note;
  return {
    value: value != null && Number.isFinite(value) ? value : null,
    source,
    note,
  };
}

function sourcedText(raw: unknown, fallback: SourcedText): SourcedText {
  if (!isRecord(raw)) return fallback;
  const source = SOURCES.includes(raw.source as PerfSource) ? (raw.source as PerfSource) : fallback.source;
  const note = typeof raw.note === "string" && raw.note.trim() ? raw.note : fallback.note;
  return {
    value: typeof raw.value === "string" ? raw.value : raw.value == null ? null : fallback.value,
    source,
    note,
  };
}

const BLANK_NUMBER: SourcedNumber = { value: null, source: "user", note: "Blank for user entry." };
const BLANK_TEXT: SourcedText = { value: null, source: "user" };

export function emptyAircraftType(icaoType: string, name = ""): AircraftTypeRecord {
  const blank = (): SourcedNumber => ({ ...BLANK_NUMBER });
  return {
    icaoType: icaoType.trim().toUpperCase(),
    origin: "user",
    name: { value: name.trim() || null, source: "user" },
    cruiseKt: blank(),
    longRangeCruiseKt: blank(),
    averageCruiseKt: blank(),
    cruiseMach: blank(),
    climbRateFpm: blank(),
    descentRateFpm: blank(),
    rangeNm: blank(),
    ferryRangeNm: blank(),
    fuelBurnLbHr: blank(),
    maxFuelLb: blank(),
    fuelDensityLbPerGal: blank(),
    mtowLb: blank(),
    mlwLb: blank(),
    mzfwLb: blank(),
    mrwLb: blank(),
    oewLb: blank(),
    maxPayloadLb: blank(),
    maxPax: blank(),
    crewSeats: blank(),
    ceilingFt: blank(),
    takeoffDistanceFt: blank(),
    landingDistanceFt: blank(),
    engines: { ...BLANK_TEXT },
    mpiIntervalHours: blank(),
    cziIntervalHours: blank(),
  };
}

function hydrateType(raw: unknown): AircraftTypeRecord | null {
  if (!isRecord(raw)) return null;
  const icaoType = String(raw.icaoType ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
  if (!icaoType) return null;
  const seed = emptyAircraftType(icaoType);
  return {
    ...seed,
    icaoType,
    origin: raw.origin === "user" ? "user" : "base",
    name: sourcedText(raw.name, seed.name),
    cruiseKt: sourcedNumber(raw.cruiseKt, seed.cruiseKt),
    longRangeCruiseKt: sourcedNumber(raw.longRangeCruiseKt, seed.longRangeCruiseKt),
    averageCruiseKt: sourcedNumber(raw.averageCruiseKt, seed.averageCruiseKt),
    cruiseMach: sourcedNumber(raw.cruiseMach, seed.cruiseMach),
    climbRateFpm: sourcedNumber(raw.climbRateFpm, seed.climbRateFpm),
    descentRateFpm: sourcedNumber(raw.descentRateFpm, seed.descentRateFpm),
    rangeNm: sourcedNumber(raw.rangeNm, seed.rangeNm),
    ferryRangeNm: sourcedNumber(raw.ferryRangeNm, seed.ferryRangeNm),
    fuelBurnLbHr: sourcedNumber(raw.fuelBurnLbHr, seed.fuelBurnLbHr),
    maxFuelLb: sourcedNumber(raw.maxFuelLb, seed.maxFuelLb),
    fuelDensityLbPerGal: sourcedNumber(raw.fuelDensityLbPerGal, seed.fuelDensityLbPerGal),
    mtowLb: sourcedNumber(raw.mtowLb, seed.mtowLb),
    mlwLb: sourcedNumber(raw.mlwLb, seed.mlwLb),
    mzfwLb: sourcedNumber(raw.mzfwLb, seed.mzfwLb),
    mrwLb: sourcedNumber(raw.mrwLb, seed.mrwLb),
    oewLb: sourcedNumber(raw.oewLb, seed.oewLb),
    maxPayloadLb: sourcedNumber(raw.maxPayloadLb, seed.maxPayloadLb),
    maxPax: sourcedNumber(raw.maxPax, seed.maxPax),
    crewSeats: sourcedNumber(raw.crewSeats, seed.crewSeats),
    ceilingFt: sourcedNumber(raw.ceilingFt, seed.ceilingFt),
    takeoffDistanceFt: sourcedNumber(raw.takeoffDistanceFt, seed.takeoffDistanceFt),
    landingDistanceFt: sourcedNumber(raw.landingDistanceFt, seed.landingDistanceFt),
    engines: sourcedText(raw.engines, seed.engines),
    mpiIntervalHours: sourcedNumber(raw.mpiIntervalHours, seed.mpiIntervalHours),
    cziIntervalHours: sourcedNumber(raw.cziIntervalHours, seed.cziIntervalHours),
  };
}

/** Shipped base. User rows are not in this file. */
export function shippedAircraftTypes(): AircraftTypeRecord[] {
  return (base as unknown[])
    .map((row) => hydrateType(row))
    .filter((row): row is AircraftTypeRecord => Boolean(row));
}

export function shippedAircraftDatabase(): AircraftDatabaseDesk {
  return { revision: 0, types: shippedAircraftTypes(), overlay: [] };
}

export function hydrateAircraftDatabase(raw: unknown): AircraftDatabaseDesk {
  const shipped = shippedAircraftDatabase();
  if (!isRecord(raw)) return shipped;
  const types = Array.isArray(raw.types)
    ? raw.types.map((row) => hydrateType(row)).filter((row): row is AircraftTypeRecord => Boolean(row))
    : shipped.types;
  const overlay = Array.isArray(raw.overlay) ? raw.overlay : [];
  const revision = Number(raw.revision);
  return {
    revision: Number.isFinite(revision) && revision > 0 ? revision : 0,
    types: types.length ? types : shipped.types,
    overlay,
  };
}

/** US gallons to pounds with the type density. Null when the density is missing. */
export function gallonsToPounds(gallons: number, densityLbPerGal: number): number | null {
  if (!Number.isFinite(gallons) || !Number.isFinite(densityLbPerGal) || densityLbPerGal <= 0) return null;
  return Math.round(gallons * densityLbPerGal);
}

export function poundsToGallons(pounds: number, densityLbPerGal: number): number | null {
  if (!Number.isFinite(pounds) || !Number.isFinite(densityLbPerGal) || densityLbPerGal <= 0) return null;
  return Math.round((pounds / densityLbPerGal) * 10) / 10;
}

export function aircraftTypeByIcao(types: readonly AircraftTypeRecord[], icaoType: string): AircraftTypeRecord | null {
  const code = icaoType.trim().toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!code) return null;
  return types.find((row) => row.icaoType.toUpperCase().replace(/[^A-Z0-9]/g, "") === code) ?? null;
}

function positive(field: SourcedNumber | undefined): number | undefined {
  const n = field?.value;
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? n : undefined;
}

/** Numbers the setup form can prefill. Empty weight stays blank. */
export type SetupPerfHint = {
  name?: string;
  cruiseKt?: number;
  longRangeCruiseKt?: number;
  averageCruiseKt?: number;
  cruiseMach?: number;
  climbRateFpm?: number;
  descentRateFpm?: number;
  rangeNm?: number;
  ferryRangeNm?: number;
  fuelBurnGph?: number;
  fuelBurnLbHr?: number;
  fuelDensityLbPerGal?: number;
  maxFuelLb?: number;
  mtowLb?: number;
  mlwLb?: number;
  mzfwLb?: number;
  mrwLb?: number;
  maxPayloadLb?: number;
  maxPax?: number;
  crewSeats?: number;
  ceilingFt?: number;
  takeoffDistanceFt?: number;
  landingDistanceFt?: number;
  engines?: string;
};

export function setupPerfFromType(row: AircraftTypeRecord): SetupPerfHint {
  const burnLb = positive(row.fuelBurnLbHr);
  const density = positive(row.fuelDensityLbPerGal);
  return {
    name: row.name.value?.trim() || undefined,
    cruiseKt: positive(row.cruiseKt),
    longRangeCruiseKt: positive(row.longRangeCruiseKt),
    averageCruiseKt: positive(row.averageCruiseKt),
    cruiseMach: positive(row.cruiseMach),
    climbRateFpm: positive(row.climbRateFpm),
    descentRateFpm: positive(row.descentRateFpm),
    rangeNm: positive(row.rangeNm),
    ferryRangeNm: positive(row.ferryRangeNm),
    fuelBurnLbHr: burnLb,
    fuelDensityLbPerGal: density,
    fuelBurnGph: burnLb != null && density != null ? burnLb / density : undefined,
    maxFuelLb: positive(row.maxFuelLb),
    mtowLb: positive(row.mtowLb),
    mlwLb: positive(row.mlwLb),
    mzfwLb: positive(row.mzfwLb),
    mrwLb: positive(row.mrwLb),
    maxPayloadLb: positive(row.maxPayloadLb),
    maxPax: positive(row.maxPax),
    crewSeats: positive(row.crewSeats),
    ceilingFt: positive(row.ceilingFt),
    takeoffDistanceFt: positive(row.takeoffDistanceFt),
    landingDistanceFt: positive(row.landingDistanceFt),
    engines: row.engines.value?.trim() || undefined,
  };
}

export function exportAircraftDatabaseJson(types: readonly AircraftTypeRecord[]): string {
  return JSON.stringify(types, null, 2);
}

/** Fields the settings page edits. Order is the form order. */
export const AIRCRAFT_TYPE_FIELDS: {
  key: keyof AircraftTypeRecord;
  label: string;
  kind: "number" | "text";
}[] = [
  { key: "name", label: "Name", kind: "text" },
  { key: "cruiseKt", label: "Cruise kt", kind: "number" },
  { key: "longRangeCruiseKt", label: "Long-range cruise kt", kind: "number" },
  { key: "averageCruiseKt", label: "Average cruise kt", kind: "number" },
  { key: "cruiseMach", label: "Cruise Mach", kind: "number" },
  { key: "climbRateFpm", label: "Climb fpm", kind: "number" },
  { key: "descentRateFpm", label: "Descent fpm", kind: "number" },
  { key: "rangeNm", label: "Range nm", kind: "number" },
  { key: "ferryRangeNm", label: "Ferry range nm", kind: "number" },
  { key: "fuelBurnLbHr", label: "Fuel burn lb/hr", kind: "number" },
  { key: "maxFuelLb", label: "Fuel capacity lb", kind: "number" },
  { key: "fuelDensityLbPerGal", label: "Fuel density lb/gal", kind: "number" },
  { key: "mtowLb", label: "MTOW lb", kind: "number" },
  { key: "mlwLb", label: "MLW lb", kind: "number" },
  { key: "mzfwLb", label: "MZFW lb", kind: "number" },
  { key: "mrwLb", label: "MRW lb", kind: "number" },
  { key: "oewLb", label: "OEW lb", kind: "number" },
  { key: "maxPayloadLb", label: "Max payload lb", kind: "number" },
  { key: "maxPax", label: "Max passengers", kind: "number" },
  { key: "crewSeats", label: "Crew seats", kind: "number" },
  { key: "ceilingFt", label: "Ceiling ft", kind: "number" },
  { key: "takeoffDistanceFt", label: "Takeoff ft", kind: "number" },
  { key: "landingDistanceFt", label: "Landing ft", kind: "number" },
  { key: "engines", label: "Engines", kind: "text" },
  { key: "mpiIntervalHours", label: "MPI hours", kind: "number" },
  { key: "cziIntervalHours", label: "CZI hours", kind: "number" },
];

export function fieldSource(row: AircraftTypeRecord, key: keyof AircraftTypeRecord): PerfSource | null {
  const value = row[key];
  if (!value || typeof value !== "object" || !("source" in value)) return null;
  return value.source;
}

/** Density the fuel planner uses. A tail figure wins. Otherwise Jet-A 6.7. */
export function fuelDensityForSpec(spec: Pick<AircraftSpec, "fuelDensityLbPerGal" | "icaoType">): number {
  const own = spec.fuelDensityLbPerGal;
  if (typeof own === "number" && Number.isFinite(own) && own > 0) return own;
  return 6.7;
}
